import React, { useMemo, useState } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useMap } from '../../contexts/MapContext';
import { useAuth } from '../../contexts/AuthContext';
import { passivePerception } from '../../lib/perception';
import { rollDice } from '../../lib/dice';

// Player-side banner. Appears when there's an unresolved perceptionPrompts
// doc targeting this player. Two affordances:
//   - "🎲 Roll d20" — active perception, longer sight range.
//   - "Use passive N" — instant, shorter range.
// Posts the result; CampaignContext writes discoveredBy for traps/tokens,
// and we apply door discoveries locally (doors live in MapContext).

export default function PerceptionPromptBanner() {
  const { perceptionPrompts, players, submitPerceptionResponse } = useCampaign();
  const { doors, updateDoor } = useMap();
  const { auth } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const playerId = auth?.playerId;
  const player   = playerId ? players?.[playerId] : null;

  const prompt = useMemo(() => {
    if (!playerId) return null;
    return Object.values(perceptionPrompts || {}).find(
      (p) => !p.resolved && (p.targetPlayerIds || []).includes(playerId) && !p.responses?.[playerId]
    );
  }, [perceptionPrompts, playerId]);

  // Snapshot of secret doors with their midpoints. Computed unconditionally
  // (above any early return) so the hook count stays stable across renders.
  // The door structure stores points as a polyline — use the segment midpoint
  // for sight checks.
  const secretDoors = useMemo(() => {
    return (doors || [])
      .filter((d) => d.type === 'secret' && d.discoveryDC != null && !d.secretRevealed && !(d.discoveredBy?.[playerId]))
      .map((d) => {
        const a = d.pts?.[0];
        const b = d.pts?.[d.pts.length - 1];
        if (!a || !b) return null;
        return {
          id: d.id,
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2,
          discoveryDC: d.discoveryDC,
          discoveredBy: d.discoveredBy,
          secretRevealed: d.secretRevealed,
        };
      })
      .filter(Boolean);
  }, [doors, playerId]);

  if (!prompt) return null;

  const passive = passivePerception(player);

  const respond = async (roll, isPassive) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await submitPerceptionResponse(prompt.id, playerId, roll, isPassive, secretDoors);
      // Door reveals are GLOBAL — once any player finds a secret door, it
      // appears to everyone as a wood, locked door. (Per-player reveal was
      // confusing in playtesting; once one PC says "look, a door", the rest
      // see it too.)
      if (result?.doorIds?.length) {
        for (const id of result.doorIds) {
          const door = doors.find((d) => d.id === id);
          if (!door) continue;
          updateDoor(id, {
            secretRevealed: true,
            style: 'wood',
            state: 'locked',
          });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoll = () => {
    const r = rollDice({ dice: [{ sides: 20, count: 1 }], modifier: passive - 10, mode: 'normal' });
    // d20 + wis mod (the -10 strips the +10 from passive to get just the modifier).
    respond(r.total, false);
  };

  const handlePassive = () => respond(passive, true);

  return (
    <div style={styles.banner}>
      <div style={styles.text}>
        <span style={styles.title}>🕵 The DM wants a perception check.</span>
      </div>
      <div style={styles.actions}>
        <button style={styles.passiveBtn} onClick={handlePassive} disabled={submitting}>
          Use passive {passive}
        </button>
        <button style={styles.rollBtn} onClick={handleRoll} disabled={submitting}>
          🎲 Roll d20
        </button>
      </div>
    </div>
  );
}

const styles = {
  banner: {
    position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
    zIndex: 50, width: 'calc(100% - 32px)', maxWidth: 480,
    background: 'linear-gradient(180deg, rgba(122,87,184,0.95), rgba(80,57,134,0.95))',
    border: '2px solid #a987d4', borderRadius: 8,
    boxShadow: '0 8px 24px rgba(122,87,184,0.4)',
    color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace",
    padding: '10px 14px',
    display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between',
    animation: 'toastSlideIn 220ms ease-out',
  },
  text:    { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  title:   { fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.04em' },
  actions: { display: 'flex', gap: 6, flexShrink: 0 },
  passiveBtn:{ padding: '8px 10px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.3)', color: '#f0e6d0', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.04em' },
  rollBtn: { padding: '8px 12px', background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', color: '#1a0e00', fontSize: 12, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.04em' },
};
